import { EndUserCertificate, EndUserError, EndUserOwnerInfo, EndUserConstants, KSPSettings, EndUser, EndUserTaxReport } from "../euscp/euscp";
import GlSign from "./GlSign";
import { downloadData, arrayToBase64, base64ToArray, signAlgoToHashAlgo, getSupportedSignAlgos, IS_DSTU_7564_DAY } from "./Utils";

/**
 * Параметры библиотеки электронной подписи
 */
export class DigitalSignatureSettings {
  /**
   * @param {string} language - Язык. Поддержываемые значения: en, ru, uk
   * @param {string} userId - id пользователя (для сохранения ключей и предпочитаемого типа ключа)
   * @param {string} httpProxyServiceURL - Ссылка на ProxyHandler
   * @param {DefaultCertificatesProvider | WebCalcCertificatesProvider} certificatesProvider - Сервис для получения сертификатов
   * @param {string} libraryUrl - ссылка на WebWorker
   */
  constructor(language, userId, httpProxyServiceURL, certificatesProvider, libraryUrl) {
    this.language = language || "en";
    this.userId = userId;
    this.httpProxyServiceURL = httpProxyServiceURL;
    this.certificatesProvider = certificatesProvider;
    this.libraryUrl = libraryUrl;
  }
}

/**
 * Фабрика настроек библиотеки
 */
export class DefaultSettingProvider {
  /**
   * @param {string} language - Язык ошибок
   * @param {string | function} userId - id пользователя (для сохранения ключей и предпочитаемого типа ключа)
   * @param {string} apiPath - путь к api ЕЦП
   * @param {string} workerPath - ссылка на WebWorker
   */
  constructor(language, userId, apiPath, workerPath) {
    if (typeof apiPath !== "string") {
      throw {
        code: EndUserError.EU_ERROR_BAD_PARAMETER,
        message: "basePath is not a string"
      };
    } else if (apiPath.endsWith("/")) {
      apiPath = apiPath.substring(0, apiPath.length - 1);
    }

    this.language = language;
    this.userId = userId;
    this.apiPath = apiPath;
    this.workerPath = workerPath;
  }

  async getGlSign() {
    return new GlSign(await downloadData(this.apiPath + "/files?name=glsign", "string"));
  }

  async getSettings() {
    if (this._settings) {
      return this._settings;
    }

    this._settings = new DigitalSignatureSettings(
      this.language,
      this.userId,
      this.apiPath + "/ProxyHandler",
      new DefaultCertificatesProvider(
        this.apiPath + "/files?name=CAs.json",
        this.apiPath + "/files?name=CACertificates.p7b"
      ),
      this.workerPath || "/euscp.worker.js"
    );

    return this._settings;
  }
}

export class WebserviceSettingProvider {
  /**
   * @param {string} language - Язык ошибок
   * @param {string | function} userId - id пользователя (для сохранения ключей и предпочитаемого типа ключа)
   * @param {string} apiPath - путь к api ЕЦП
   * @param {string} workerPath - ссылка на WebWorker
   */
  constructor(language, userId, apiPath, workerPath) {
    this.language = language;
    this.userId = userId;
    this.apiPath = apiPath;
    this.workerPath = workerPath;
  }

  async getGlSign() {
    return new GlSign(await downloadData(`${this.apiPath}/webservice.asmx/ExecuteEx?pureJSON=`, undefined, undefined, { calcId: "GETGLSIGNBEFORELOGIN", args: "", ticket: "" }));
  }

  async getSettings() {
    if (this._settings) {
      return this._settings;
    }

    const json = await downloadData(`${this.apiPath}/webservice.asmx/ExecuteEx?pureJSON=`, "json", undefined, { calcId: "GETCASANDCERTIFICATEFILES", args: "", ticket: "" });
    this._settings = new DigitalSignatureSettings(
      this.language,
      this.userId,
      `${this.apiPath}/ProxyHandler`,
      new DefaultCertificatesProvider(
        `${this.apiPath}/GetFile.ashx?file=${json.FILECAS}`,
        `${this.apiPath}/GetFile.ashx?file=${json.FILECERT}`
      ),
      this.workerPath || "/euscp.worker.js"
    );

    return this._settings;
  }
}

export class LegacySettingsProvider {
  /**
   * @param {string} language - Язык ошибок
   * @param {string | function} userId - id пользователя (для сохранения ключей и предпочитаемого типа ключа)
   * @param {string} basePath - путь к api ЕЦП
   * @param {string} glsign - ПГУ GlSign
   * @param {string} workerPath - ссылка на WebWorker
   */
  constructor(language, userId, basePath, glsign, workerPath) {
    if (typeof basePath !== "string") {
      throw {
        code: EndUserError.EU_ERROR_BAD_PARAMETER,
        message: "basePath is not a string"
      };
    } else if (basePath.endsWith("/")) {
      basePath = basePath.substring(0, basePath.length - 1);
    }

    this.language = language;
    this.userId = userId;
    this.basePath = basePath;
    this.glsign = glsign;
    this.workerPath = workerPath;
  }

  getGlSign() {
    return typeof this.glsign === "string" ? new GlSign(this.glsign) : this.glsign;
  }

  getSettings(testMode) {
    return new DigitalSignatureSettings(this.language,
      this.userId,
      this.basePath + "/ProxyHandler.ashx",
      new LegacyCertificatesProvider(
        testMode,
        this.basePath + "/Data/version.txt",
        this.basePath + (testMode ? "/Data/CAs.Test.json" : "/Data/CAs.json"),
        this.basePath + (testMode ? "/Data/CACertificates.Test.p7b" : "/Data/CACertificates.p7b")
      ),
      this.workerPath || "/euscp.worker.js"
    );
  }
}

export class CertificatesProvider {
  /**
   * @param {boolean} testMode - Тестовый режим
   */
  constructor(testMode) {
    this.testMode = testMode;
  }

  /**
   * Загрузить список ЦСК и корневые сертификаты
   * @returns {Promise}
   */
  async loadCertificates() {
    throw {
      code: EndUserError.EU_ERROR_NOT_SUPPORTED,
      message: "Not implemented"
    };
  }

  get _storagePrefix() {
    return this.testMode ? "Test" : "";
  }

  _getItem(key) {
    return localStorage.getItem(this._storagePrefix + key);
  }

  _setItem(key, value) {
    localStorage.setItem(this._storagePrefix + key, value);
  }

  _removeItem(key) {
    localStorage.removeItem(this._storagePrefix + key);
  }
}

/**
 * Класс для загрузки сертификатов по ссылкам
 */
export class DefaultCertificatesProvider extends CertificatesProvider {
  /**
   * @param {boolean} testMode - Тестовый режим
   * @param {string} versionUri - Ссылка на файл с текущей версией сертификатов
   * @param {string} CAsUri - Ссылка на файл со списком ЦСК
   * @param {string} CACertificatesUri - Ссылка на файл с корневыми сертификатами ЦСК
   */
  constructor(CAsUri, CACertificatesUri) {
    super(false);
    this.CAsUri = CAsUri;
    this.CACertificatesUri = CACertificatesUri;
  }

  async loadCertificates() {
    try {
      const [CAs, CACertificates] = await Promise.all([downloadData(this.CAsUri, "json"), downloadData(this.CACertificatesUri, "binary")]);

      return { CAs, CACertificates };
    } catch (error) {
      console.error(error);
      throw {
        code: EndUserError.EU_ERROR_DOWNLOAD_FILE,
        message: "Error downloading root certificates"
      };
    }
  }
}

export class LegacyCertificatesProvider extends CertificatesProvider {
  /**
   * @param {boolean} testMode - Тестовый режим
   * @param {string} versionUri - Ссылка на файл с текущей версией сертификатов
   * @param {string} CAsUri - Ссылка на файл со списком ЦСК
   * @param {string} CACertificatesUri - Ссылка на файл с корневыми сертификатами ЦСК
   */
  constructor(testMode, versionUri, CAsUri, CACertificatesUri) {
    super(testMode);
    this.versionUri = versionUri;
    this.CAsUri = CAsUri;
    this.CACertificatesUri = CACertificatesUri;
  }

  async loadCertificates() {
    let CAs = this._getItem("CA"),
      CACertificates = this._getItem("Certificates"),
      CACertificatesVersion = this._getItem("CAVersion");

    try {
      const currentVersion = await downloadData(this.versionUri);
      if (currentVersion != CACertificatesVersion) {
        CACertificatesVersion = currentVersion;
        CAs = null;
        CACertificates = null;
      }

      if (CAs) {
        try {
          CAs = JSON.parse(CAs);
        } catch (error) {
          console.error(error);
          CAs = null;
        }
      }
      if (!CAs) {
        CAs = await downloadData(this.CAsUri, "json");
        this._setItem("CA", JSON.stringify(CAs));
      }

      if (CACertificates) {
        try {
          CACertificates = base64ToArray(CACertificates);
        } catch (error) {
          console.error(error);
          CACertificates = null;
        }
      }
      if (!CACertificates) {
        CACertificates = await downloadData(this.CACertificatesUri, "binary");
        this._setItem("Certificates", arrayToBase64(CACertificates));
      }
      this._setItem("CAVersion", CACertificatesVersion);
      return { CAs, CACertificates };
    } catch (error) {
      this._removeItem("CA");
      this._removeItem("CAVersion");
      this._removeItem("Certificates");
      console.error(error);
      throw {
        code: EndUserError.EU_ERROR_DOWNLOAD_FILE,
        message: "Error downloading root certificates"
      };
    }
  }
}

/** Подпись файловыми ключами, через облачные сервисы  */
export const DigitalSignatureLibraryTypeJS = 0;
/** Подпись аппартными ключами */
export const DigitalSignatureLibraryTypeSW = 1;

/**
 * Типы библиотеки
 */
export const DigitalSignatureLibraryType = {
  /** Подпись файловыми ключами и через облачные сервисы */
  JS: DigitalSignatureLibraryTypeJS,
  /** Подпись аппартными ключами */
  SW: DigitalSignatureLibraryTypeSW
};

/** Файловый ключ */
export const DigitalSignatureKeyTypeFile = 0;
/** Аппаратный ключ */
export const DigitalSignatureKeyTypeToken = 1;
/** Облачный сервис */
export const DigitalSignatureKeyTypeKSP = 2;
/** Хранилище сертификатов Windows */
export const DigitalSignatureKeyTypeWindowsCertStore = 3;
/** UmCAService */
export const DigitalSignatureKeyTypeUmCAService = 4;

/**
 * Типы ключей
 */
export const DigitalSignatureKeyType = {
  File: DigitalSignatureKeyTypeFile,
  Token: DigitalSignatureKeyTypeToken,
  KSP: DigitalSignatureKeyTypeKSP,
  WindowsCertStore: DigitalSignatureKeyTypeWindowsCertStore,
  UmCAService: DigitalSignatureKeyTypeUmCAService
};

export const EU_KSP_VTOKEN = -2;
export const EU_KSP_GRYADA = -1;
export const EU_KSP_IIT = 4;
export const EU_KSP_PB = 6;
export const EU_KSP_DIIA = 7;

export const EndUserKSP = {
  IIT: EU_KSP_IIT,
  PB: EU_KSP_PB,
  DIIA: EU_KSP_DIIA,
  GRYADA: EU_KSP_GRYADA,
  VTOKEN: EU_KSP_VTOKEN
};

export const EU_KSP_ClientIdType = -1;

export const EndUserKSPClientIdType = {
  Default: EndUserConstants.EndUserKSPClientIdType.Default,
  Name: EndUserConstants.EndUserKSPClientIdType.Name,
  Phone: EndUserConstants.EndUserKSPClientIdType.Phone,
  Email: EndUserConstants.EndUserKSPClientIdType.Email,
  NamePassword: EU_KSP_ClientIdType
};

export class PrivateKeyInfo {
  /**
   * @param {number} keyType - Тип ключа
   * @param {EndUserOwnerInfo} ownerInfo - Информация о владельце ключа
   * @param {EndUserCertificate[]} certificates - Сертификаты ключа
   * @param {string} visibleName - Имя ключа
   * @param {string} id - Идентификатор ключа
   */
  constructor(keyType, ownerInfo, certificates, visibleName, id) {
    this.keyType = keyType;
    this.ownerInfo = ownerInfo;
    this.certificates = certificates;
    this.visibleName = visibleName;
    this.id = id;
  }

  /**
   * Является ли ключ цифровой печаткой
   */
  isDigitalStamp() {
    if (!Array.isArray(this.certificates) || this.certificates.length === 0) {
      return false;
    }
    const certificates = this.certificates;
    for (let certificate = 0; certificate < certificates.length; certificate++) {
      const infoEx = certificates[certificate].infoEx;
      if (infoEx.publicKeyType == EndUserConstants.EndUserCertKeyType.DSTU4145
        && (infoEx.keyUsageType & EndUserConstants.EndUserKeyUsage.DigitalSignature) == EndUserConstants.EndUserKeyUsage.DigitalSignature) {
        return !(infoEx.extKeyUsages.indexOf(EndUserConstants.EU_UA_OID_EXT_KEY_USAGE_STAMP) < 0 && infoEx.extKeyUsages.indexOf("Цифрова печатка") < 0);
      }
    }
    return false;
  }

  /**
   * Ключ поддерживает электронную подпись
   */
  supportSigning() {
    if (this.keyType === DigitalSignatureKeyTypeKSP) {
      return true;
    }

    const certificates = this.certificates;
    for (let certificate = 0; certificate < certificates.length; certificate++) {
      const infoEx = certificates[certificate].infoEx;
      if ((infoEx.keyUsageType & EndUserConstants.EndUserKeyUsage.DigitalSignature) == EndUserConstants.EndUserKeyUsage.DigitalSignature) {
        return true;
      }
    }
    return false;
  }

  /**
   * Ключ поддерживает шифрование
   */
  supportEncryption() {
    if (this.keyType === DigitalSignatureKeyTypeKSP) {
      return false;
    }

    const certificates = this.certificates;
    for (let certificate = 0; certificate < certificates.length; certificate++) {
      const infoEx = certificates[certificate].infoEx;
      if ((infoEx.keyUsageType & EndUserConstants.EndUserKeyUsage.KeyAgreement) == EndUserConstants.EndUserKeyUsage.KeyAgreement) {
        return true;
      }
    }
    return false;
  }

  /**
   * Получить алгоритм подписи
   */
  getSignAlgo() {
    const certificates = this.certificates;
    if (this.keyType === DigitalSignatureKeyTypeKSP) {
      const ksp = this.ksp;
      if (Array.isArray(ksp.signAlgos) && ksp.signAlgos.length > 0) {
        const signAlgo = ksp.signAlgos[0];
        if (signAlgo === EndUserConstants.EndUserSignAlgo.DSTU4145WithDSTU7564 && this._glsign?.HashAlgorithm === "GOST34311" && ksp.signAlgos.includes(EndUserConstants.EndUserSignAlgo.DSTU4145WithGOST34311)) {
          return EndUserConstants.EndUserSignAlgo.DSTU4145WithGOST34311;
        }
        return signAlgo;
      }
      return EndUserConstants.EndUserSignAlgo.DSTU4145WithGOST34311;
    }
    else if (certificates.length > 0) {
      return getSupportedSignAlgos(certificates, this._glsign)[0];
    }
    else {
      return EndUserConstants.EndUserSignAlgo.Unknown;
    }
  }

  /**
   * Получить алгоритм хеширования
   */
  getHashAlgo() {
    if (this.keyType === DigitalSignatureKeyTypeKSP) {
      return signAlgoToHashAlgo(this.getSignAlgo());
    }

    const certificates = this.certificates;
    if (certificates.length > 0 && certificates[0]?.infoEx?.certHashType) {
      const hashType = certificates[0].infoEx.certHashType;

      if (hashType == EndUserConstants.EndUserHashAlgo.GOST34311 && this._glsign?.HashAlgorithm !== "GOST34311") {
        return EndUserConstants.EndUserHashAlgo.DSTU7564_256;
      }
      if (hashType == EndUserConstants.EndUserHashAlgo.DSTU7564_256 && this._glsign?.HashAlgorithm === "GOST34311") {
        return EndUserConstants.EndUserHashAlgo.GOST34311;
      }

      return hashType;
    }
    return signAlgoToHashAlgo(this.getSignAlgo());
  }
}

export class FilePrivateKeyInfo extends PrivateKeyInfo {
  /**
   * @param {number} keyType - Тип ключа
   * @param {EndUserOwnerInfo} ownerInfo - Информация о владельце ключа
   * @param {EndUserCertificate[]} certificates - Сертификаты ключа
   * @param {Uint8Array} privateKey - Файловый ключ
   * @param {string?} password - Пароль от ключа
   */
  constructor(keyType, ownerInfo, certificates, privateKey, password) {
    super(keyType, ownerInfo, certificates, certificates[0].infoEx.subjCN, certificates[0].infoEx.serial);
    this.privateKey = privateKey;
    this.password = password;
  }
}

export class HardwarePrivateKeyInfo extends PrivateKeyInfo {
  /**
   * @param {number} keyType - Тип ключа
   * @param {EndUserOwnerInfo} ownerInfo - Информация о владельце ключа
   * @param {EndUserCertificate[]} certificates - Сертификаты ключа
   * @param {EndUserKeyMedia} keyMedia - Параметры аппаратного ключа
   * @param {boolean} needSavePassword - Нужно ли сохранять пароль
   */
  constructor(keyType, ownerInfo, certificates, keyMedia) {
    super(keyType, ownerInfo, certificates, certificates[0].infoEx.subjCN, certificates[0].infoEx.serial);
    this.keyMedia = keyMedia;
  }
}

export class KspPrivateKeyInfo extends PrivateKeyInfo {
  /**
   * @param {number} keyType - Тип ключа
   * @param {EndUserOwnerInfo} ownerInfo - Информация о владельце ключа
   * @param {EndUserCertificate[]} certificates - Сертификаты ключа
   * @param {string} userId - Идентификатор пользователя
   * @param {EndUserKSP} ksp - Объект KSP
   */
  constructor(keyType, ownerInfo, certificates, userId, ksp, keyId) {
    super(keyType, ownerInfo, certificates, userId, userId);
    this.userId = userId;
    this.ksp = ksp;
    this.keyId = keyId;
  }
}

export function getDefaultKSPs(allowTest) {
  const depositsign = new KSPSettings();
  depositsign.id = "DEPOSITSIGN";
  depositsign.name = "DepositSign";
  depositsign.ksp = EndUserConstants.EU_KSP_PB;
  depositsign.address = "https://depositsign.com/api/v1/it-enterprise/sign-server";
  depositsign.clientIdPrefix = "";
  depositsign.directAccess = true;
  depositsign.needQRCode = false;

  const diia = new KSPSettings();
  diia.id = "DIIA-SIGN";
  diia.name = "Дія.Підпис";
  diia.ksp = EndUserConstants.EU_KSP_DIIA;
  diia.directAccess = false;
  diia.mobileAppName = "Дія";
  diia.address = "https://diia-sign.it.ua/KSPSign";
  diia.systemId = "diia-sign-it-ent";
  diia.needQRCode = true;
  diia.signAlgos = [1];

  const smartId = new KSPSettings();
  smartId.id = "PB-SMARTID";
  smartId.name = "Приватбанк - хмарний підпис \"SmartID\"";
  smartId.ksp = EndUserConstants.EU_KSP_PB;
  smartId.directAccess = true;
  smartId.mobileAppName = "Приват24";
  smartId.address = "https://acsk.privatbank.ua/cloud/api/back/";
  smartId.clientIdPrefix = "IEIS_";
  smartId.confirmationURL = "https://www.privat24.ua/rd/kep";
  smartId.needQRCode = true;

  const vchasno = new KSPSettings();
  vchasno.id = "VCHASNO";
  vchasno.name = "Вчасно - хмарний підпис";
  vchasno.ksp = EndUserConstants.EU_KSP_PB;
  vchasno.address = "https://cs.vchasno.ua/ss/";
  vchasno.clientIdPrefix = "";
  vchasno.directAccess = false;
  vchasno.needQRCode = false;

  const vchasnoQR = new KSPSettings();
  vchasnoQR.id = "VCHASNOQR";
  vchasnoQR.name = "Вчасно - хмарний підпис (QR)";
  vchasnoQR.ksp = EndUserConstants.EU_KSP_PB;
  vchasnoQR.address = "https://cs.vchasno.ua/ss/";
  vchasnoQR.clientIdPrefix = "vchasno_";
  vchasnoQR.confirmationURL = "https://cs.vchasno.ua/rd/";
  vchasnoQR.mobileAppName = "Вчасно.КЕП";
  vchasnoQR.directAccess = false;
  vchasnoQR.needQRCode = true;

  const cloudKey = new KSPSettings();
  cloudKey.id = "CLOUDKEY";
  cloudKey.name = "ТОВ «ЦСК «Україна» - хмарний підпис CloudKey";
  cloudKey.ksp = EndUserConstants.EU_KSP_PB;
  cloudKey.directAccess = true;
  cloudKey.mobileAppName = "CloudKey";
  cloudKey.address = "https://sid.uakey.com.ua/smartid/iit/";
  cloudKey.clientIdPrefix = "DIIA_2";
  cloudKey.confirmationURL = "https://sid.uakey.com.ua/kep?hash=rd/kep";
  cloudKey.needQRCode = true;

  const esign = new KSPSettings();
  esign.id = "ESIGN";
  esign.name = "ESign - хмарний підпис";
  esign.ksp = EndUserConstants.EU_KSP_PB;
  esign.address = "https://cabinet.e-life.com.ua/api/EDG/Sign";
  esign.clientIdPrefix = "";
  esign.directAccess = true;
  esign.needQRCode = false;

  const smartSignTax = new KSPSettings();
  smartSignTax.id = "SMARTSIGNTAX";
  smartSignTax.name = "ДПС - хмарний підпис";
  smartSignTax.ksp = EndUserConstants.EU_KSP_PB;
  smartSignTax.address = "https://smart-sign.tax.gov.ua/";
  smartSignTax.port = "443";
  smartSignTax.directAccess = true;
  smartSignTax.clientIdType = EndUserKSPClientIdType.Name;
  smartSignTax.needQRCode = false;

  const pumb = new KSPSettings();
  pumb.id = "PUMB";
  pumb.name = "ПУМБ - хмарний підпис";
  pumb.ksp = EndUserConstants.EU_KSP_PB;
  pumb.address = "https://apiext.pumb.ua/hogsmeade/striga/v1";
  pumb.directAccess = false;
  pumb.clientIdPrefix = "SMARTTENDER_";
  pumb.confirmationURL = "https://www.pumb.ua/qes";
  pumb.mobileAppName = "";
  pumb.needQRCode = true;

  const ugb = new KSPSettings();
  ugb.id = "UGB";
  ugb.name = "Укргазбанк - хмарний підпис «EcoSign»";
  ugb.ksp = EndUserKSP.VTOKEN;
  ugb.address = "https://vtms-api-qca.ukrgasbank.com/vtco/api/v1";
  ugb.systemId = "cihsmVtcoServiceClientSmartTender";
  ugb.directAccess = true;
  ugb.clientIdType = EndUserKSPClientIdType.NamePassword;
  ugb.needQRCode = false;

  const alliance = new KSPSettings();
  alliance.id = "ALLIANCE";
  alliance.name = "Банк Альянс - хмарний підпис";
  alliance.ksp = EndUserKSP.VTOKEN;
  alliance.address = "https://cihsm-api.bankalliance.ua/vtco/api/v1";
  alliance.systemId = "cihsmVtcoServiceClientSmartTender";
  alliance.directAccess = true;
  alliance.clientIdType = EndUserKSPClientIdType.NamePassword;
  alliance.needQRCode = false;

  const vtoken = new KSPSettings();
  vtoken.id = "VTOKEN";
  vtoken.name = "Хмарний сервіс Шифр-vToken (тестовий)";
  vtoken.ksp = EndUserKSP.VTOKEN;
  vtoken.address = "https://cihsm-dev-api.cipher.com.ua/vtco/api/v1";
  vtoken.systemId = "cipherVTokenIntegrationClient";
  vtoken.directAccess = true;
  vtoken.clientIdType = EndUserKSPClientIdType.NamePassword;
  vtoken.needQRCode = false;

  const amokey = new KSPSettings();
  amokey.id = "AMOKEY";
  amokey.name = "АМО ФІНТЕХ – хмарний підпис AMOKEY";
  amokey.ksp = EndUserKSP.PB;
  amokey.address = "https://sserver.amokey.com";
  amokey.port = "443";
  amokey.clientIdPrefix = "";
  amokey.directAccess = true;
  amokey.clientIdType = EndUserKSPClientIdType.Name;
  amokey.needQRCode = false;

  const tascombank = new KSPSettings();
  tascombank.id = "TASCOMBANK";
  tascombank.name = "Сервер підпису КНЕДП АТ \"ТАСКОМБАНК\"";
  tascombank.ksp = EndUserKSP.PB;
  tascombank.address = "https://smartsign.tascombank.ua";
  tascombank.port = "443";
  tascombank.clientIdPrefix = "";
  tascombank.directAccess = true;
  tascombank.clientIdType = EndUserKSPClientIdType.Name;
  tascombank.needQRCode = false;

  const edinQr = new KSPSettings();
  edinQr.id = "EDINQR";
  edinQr.name = "EDIN ID - хмарний підпис (QR)";
  edinQr.ksp = EndUserKSP.PB;
  edinQr.address = "https://id.edin.ua/api/ess/v1/";
  edinQr.confirmationURL = "https://id.edin.ua/mobile/sign";
  edinQr.port = "";
  edinQr.clientIdPrefix = "EDIN_";
  edinQr.directAccess = true;
  edinQr.mobileAppName = "edinid";
  edinQr.needQRCode = true;

  const edin = new KSPSettings();
  edin.id = "EDIN";
  edin.name = "EDIN ID - хмарний підпис";
  edin.ksp = EndUserKSP.PB;
  edin.address = "https://id.edin.ua/api/ess/v1/";
  edin.port = "";
  edin.clientIdPrefix = "";
  edin.directAccess = true;
  edin.clientIdType = EndUserKSPClientIdType.Name;
  edin.needQRCode = false;

  const ukrsibbank = new KSPSettings();
  ukrsibbank.id = "UKRSIBBANK";
  ukrsibbank.name = "УКРСИББАНК - хмарний підпис";
  ukrsibbank.ksp = EndUserKSP.PB;
  ukrsibbank.address = "https://business.ukrsibbank.com/morpheus/smart-sign/";
  ukrsibbank.confirmationURL = "https://business.ukrsibbank.com/smartsign";
  ukrsibbank.port = "";
  ukrsibbank.clientIdPrefix = "USB_";
  ukrsibbank.directAccess = true;
  ukrsibbank.mobileAppName = "UKRSIB business";
  ukrsibbank.needQRCode = true;

  const ksps = [
    depositsign,
    diia,
    smartId,
    vchasno,
    vchasnoQR,
    cloudKey,
    esign,
    smartSignTax,
    pumb,
    ugb,
    alliance,
    amokey,
    tascombank,
    edinQr,
    edin,
    ukrsibbank
  ];

  if (allowTest) {
    ksps.push(vtoken);
  }

  return ksps;
}

/**
 * Ответ на запрос получения списка ключей пользователя в облачного провайдера
 */
export class KSPKeyMediasResponse {
  /**
   * Ответ на запрос получения списка ключей пользователя в облачного провайдера
   * @param {string} token Токен авторизации
   * @param {boolean} isTwoFactor Требуется ли второй фактор для подписания
   * @param {KSPKeyMedia[]} keys Спикок ключей, доступных пользователю
   */
  constructor(token, isTwoFactor, keys) {
    this.token = token;
    this.isTwoFactor = isTwoFactor;
    this.keys = keys;
  }
}

/**
 * Информация про ключ пользователя облачного провайдера
 */
export class KSPKeyMedia {

  /**
   * Информация про ключ пользователя облачного провайдера
   * @param {string} keyId Идентификатор ключа
   * @param {string} visibleName Отображаемое имя ключа
   * @param {string} subject Владелец ключа
   * @param {string} issuer ЦСК, который выдал ключ
   * @param {boolean} isStamp Является ли ключ цифровой печатью
   */
  constructor(keyId, visibleName, subject, issuer, isStamp) {
    this.keyId = keyId;
    this.visibleName = visibleName;
    this.subject = subject;
    this.issuer = issuer;
    this.isStamp = isStamp;
  }
}

/**
 * Данные для авторизации пользователя облачного провайдера
 */
export class KSPUserAuthData {
  /**
   * Данные для авторизации пользователя облачного провайдера
   * @param {string} userName Имя пользователя
   * @param {string} password Пароль от аккаунта
   */
  constructor(userName, password) {
    this.userName = userName;
    this.password = password;
  }
}

/**
 * Данные для авторизации ключа облачного провайдера
 */
export class KSPPrivateKeyAuthData {
  /**
   *
   * @param {string} pin Пароль от ключа
   * @param {string} token Токен авторизации аккаунта
   * @param {string} twoFactorCode Код двухфакторной авторизации
   */
  constructor(pin, token, twoFactorCode) {
    this.pin = pin;
    this.token = token;
    this.twoFactorCode = twoFactorCode;
  }
}

export class ProtectTaxReportParams {
  /**
   * Protect tax reports
   * @param {PrivateKeyInfo} accountantPrivateKeyContext The accountant private key context using for sign report. Optional parameter.
   * @param {PrivateKeyInfo | Array<PrivateKeyInfo>} directorPrivateKeyContext The director private key context (or array of 2 private key contexts if key for sign and envelop stored separately) using for sign and envelop(if no stamp) report. Optional parameter.
   * @param {PrivateKeyInfo | Array<PrivateKeyInfo>} stampPrivateKeyContext The stamp private key context (or array of 2 private key contexts if key for sign and envelop stored separately) using for sign and envelop report. Optional parameter.
   * @param {string} senderEMail The sender EMail.
   * @param {Uint8Array} recipientCertificate The recipient certificate.
   * @param {Array<EndUserTaxReport>} reports The reports to protect.
   * @param {boolean} returnIntermediateSign The boolean value that specifies whether to return signed report (intermediate result of report protection before evenloping).
   */
  constructor(accountantPrivateKeyContext, directorPrivateKeyContext, stampPrivateKeyContext, senderEMail, recipientCertificate, reports, returnIntermediateSign) {
    this.accountantPrivateKeyContext = accountantPrivateKeyContext;
    this.directorPrivateKeyContext = directorPrivateKeyContext;
    this.stampPrivateKeyContext = stampPrivateKeyContext;
    this.senderEMail = senderEMail;
    this.recipientCertificate = recipientCertificate;
    this.reports = reports;
    this.returnIntermediateSign = returnIntermediateSign;
  }
}
